"use client"

import Heading from "@/app/(site)/_components/Atoms/Heading"
import ButtonPill from "@/app/(site)/_components/Molecules/ButtonPill"
import logger from "@/lib/logger/logger-init"
import { NEXT_NOT_FOUND_ERROR } from "@/lib/siteconfig/constants"
import React, { Component, type ErrorInfo, type ReactNode } from "react"

/**
 * References:
 * Vercel: https://github.com/vercel/next.js/blob/0bd5fd08309bebcdfb1d4c855c1f2e81a1aed97b/docs/01-app/04-api-reference/03-file-conventions/error.mdx#graceful-error-recovery-with-a-custom-error-boundary
 * NextJs: https://nextjs.org/docs/14/app/building-your-application/routing/error-handling
 *
 * On a full document load this keeps the SSR HTML if hydration throws (the original
 * "graceful degrade"). On client-side navigation there is no committed HTML in the
 * ref — previously we painted an empty string, which left only the layout footer
 * on screen. That empty fallback is what made PDP crashes look like a newsletter page.
 */

interface ErrorBoundaryProps {
    children: ReactNode
    onError?: (error: Error, errorInfo: ErrorInfo) => void
    /** Change this to remount/reset after a recovered navigation (e.g. product slug). */
    resetKey?: string
}

interface ErrorBoundaryState {
    hasError: boolean
    error: ErrorWithDigest | null
}

type ErrorWithDigest = Error & { digest?: string }

const shouldShowErrorDetails = process.env.NEXT_PUBLIC_ENVIRONMENT !== "production"

export class GracefullyDegradingErrorBoundary extends Component<
    ErrorBoundaryProps,
    ErrorBoundaryState
> {
    private contentRef: React.RefObject<HTMLDivElement | null>

    constructor(props: ErrorBoundaryProps) {
        super(props)
        this.state = { hasError: false, error: null }
        this.contentRef = React.createRef()
    }

    static getDerivedStateFromError(error: ErrorWithDigest): ErrorBoundaryState {
        const hasError = error?.digest !== NEXT_NOT_FOUND_ERROR
        return { hasError, error: hasError ? error : null }
    }

    componentDidCatch(error: Error, errorInfo: ErrorInfo) {
        const href = typeof window !== "undefined" ? window.location.href : ""

        console.error("[GracefullyDegradingErrorBoundary]", error, errorInfo)
        logger.error("Client render error in GracefullyDegradingErrorBoundary", {
            message: error.message,
            stack: error.stack,
            componentStack: errorInfo.componentStack,
            href
        })

        if (typeof window !== "undefined") {
            window.__POC_LAST_RENDER_ERROR__ = {
                message: error.message,
                stack: error.stack,
                componentStack: errorInfo.componentStack,
                href,
                digest: (error as ErrorWithDigest).digest
            }
        }

        this.props.onError?.(error, errorInfo)
    }

    componentDidUpdate(prevProps: ErrorBoundaryProps) {
        if (prevProps.resetKey !== this.props.resetKey && this.state.hasError) {
            this.setState({ hasError: false, error: null })
        }
    }

    private handleReset = () => {
        this.setState({ hasError: false, error: null })
    }

    render() {
        if (this.state.hasError) {
            const committedHtml = this.contentRef.current?.innerHTML

            // Full page load: keep the server HTML so the product is still visible.
            if (committedHtml) {
                return (
                    <div
                        ref={this.contentRef}
                        suppressHydrationWarning
                        dangerouslySetInnerHTML={{ __html: committedHtml }}
                    />
                )
            }

            // Client-side navigation: nothing to preserve. Show a visible error
            // instead of an empty main (which previously revealed only the footer).
            return (
                <div className="flex flex-col items-center p-24 py-164 md:py-264">
                    <Heading type="h1" className="mb-16 text-center md:mb-32">
                        It looks like <br />
                        something went wrong
                    </Heading>
                    {shouldShowErrorDetails && this.state.error?.message && (
                        <pre className="mb-24 max-w-full overflow-x-auto px-16 text-left text-14 text-col-text-alert">
                            {this.state.error.message}
                        </pre>
                    )}
                    <ButtonPill
                        type="button"
                        aria-label="Try again"
                        color="black"
                        size="large"
                        variant="primary"
                        onClick={this.handleReset}
                    >
                        Try again
                    </ButtonPill>
                </div>
            )
        }

        return <div ref={this.contentRef}>{this.props.children}</div>
    }
}

export default GracefullyDegradingErrorBoundary
